<template>
  <div v-if="canUseChat" class="pointer-events-none fixed inset-x-2 bottom-0 z-50 flex items-end justify-end gap-2 sm:right-4 sm:left-auto">
    <ChatConversationWindow v-for="conversation in visibleOpenConversations" :key="conversation.id" :conversation="conversation" />

    <Popover v-model:open="isLauncherOpen">
      <PopoverTrigger as-child>
        <Button class="pointer-events-auto mb-4 shrink-0 shadow-lg" size="lg" :aria-label="totalUnreadCount > 0 ? `Open chats, ${totalUnreadCount} unread messages` : 'Open chats'">
          <MessagesSquare class="h-5 w-5" />
          <span class="hidden sm:inline">Chats</span>
          <Badge v-if="totalUnreadCount > 0" variant="secondary" class="min-w-5 justify-center px-1.5 py-0 text-[10px]">
            {{ totalUnreadCount }}
          </Badge>
          <span v-if="hiddenOpenConversationCount > 0" class="text-xs">+{{ hiddenOpenConversationCount }}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" side="top" class="pointer-events-auto mb-2 w-[min(22rem,calc(100vw-2rem))] p-0">
        <div class="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 class="font-semibold">
              Chats
            </h2>
            <p class="text-xs text-muted-foreground">
              Open a conversation anywhere in the app.
            </p>
          </div>
          <Button variant="ghost" size="icon-sm" :disabled="conversationStore.isLoading" aria-label="Refresh conversations" @click="loadConversations">
            <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': conversationStore.isLoading }" />
          </Button>
        </div>

        <div class="border-b p-3">
          <div class="relative">
            <Search class="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input v-model="searchQuery" class="pl-9" placeholder="Search conversations" />
          </div>
        </div>

        <Alert v-if="dockError" variant="destructive" class="m-3 mb-0">
          <AlertDescription class="text-xs">
            {{ dockError }}
          </AlertDescription>
        </Alert>

        <ScrollArea class="h-80">
          <div v-if="conversationStore.isLoading && conversations.length === 0" class="flex flex-col gap-2 p-3">
            <Skeleton v-for="index in 5" :key="index" class="h-14 w-full rounded-md" />
          </div>
          <div v-else-if="filteredConversations.length === 0" class="p-6 text-center text-sm text-muted-foreground">
            No conversations found.
          </div>
          <div v-else class="flex flex-col gap-1 p-2">
            <button v-for="conversation in filteredConversations" :key="conversation.id" type="button" class="flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent" :class="hasUnreadMessages(conversation) ? 'bg-accent/60' : ''" :disabled="isUpdatingWindow(conversation.id)" @click="openConversation(conversation)">
              <Avatar size="sm" :class="hasUnreadMessages(conversation) ? 'ring-2 ring-primary/40' : ''">
                <AvatarImage v-if="conversation.avatar_path" :src="conversation.avatar_path" />
                <AvatarFallback>{{ conversationInitials(conversation) }}</AvatarFallback>
              </Avatar>
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-2">
                  <span class="min-w-0 flex-1 truncate text-sm" :class="hasUnreadMessages(conversation) ? 'font-semibold text-foreground' : 'font-medium'">{{ conversationTitle(conversation) }}</span>
                  <LoaderCircle v-if="isUpdatingWindow(conversation.id)" class="h-3.5 w-3.5 shrink-0 animate-spin" />
                  <Badge v-else-if="hasUnreadMessages(conversation)" class="min-w-5 shrink-0 justify-center px-1.5 py-0 text-[10px]">{{ unreadMessageCount(conversation) }}</Badge>
                </span>
                <span class="block truncate text-xs" :class="hasUnreadMessages(conversation) ? 'font-medium text-foreground' : 'text-muted-foreground'">{{ lastMessagePreview(conversation) }}</span>
              </span>
            </button>
          </div>
        </ScrollArea>

        <div class="border-t p-2">
          <RouterLink :to="{ name: 'ChatIndexPage' }" class="flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent" @click="isLauncherOpen = false">
            Open all chats
          </RouterLink>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { LoaderCircle, MessagesSquare, RefreshCw, Search } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../../resources/js/components/ui/popover/index.js'
import { ScrollArea } from '../../../../../../../resources/js/components/ui/scroll-area/index.js'
import { Skeleton } from '../../../../../../../resources/js/components/ui/skeleton/index.js'
import { useAuthStore } from '../../../../../../../resources/js/stores/auth.js'
import { useBrowserNotificationStore } from '../../stores/browserNotificationStore.js'
import { useChatStore } from '../../stores/chatStore.js'
import { useConversationParticipantStore } from '../../stores/conversationParticipantStore.js'
import { useConversationStore } from '../../stores/conversationStore.js'
import ChatConversationWindow from './ChatConversationWindow.vue'

const route = useRoute()
const authStore = useAuthStore()
const browserNotificationStore = useBrowserNotificationStore()
const chatStore = useChatStore()
const conversationStore = useConversationStore()
const participantStore = useConversationParticipantStore()

const { items: conversations } = storeToRefs(conversationStore)
const dockError = ref('')
const isLauncherOpen = ref(false)
const searchQuery = ref('')
const maximumVisibleWindows = ref(3)
const hasInitializedChat = ref(false)
const preferredConversationId = ref(null)

const canUseChat = computed(() => authStore.hasPermission('chat access'))
const routeConversationId = computed(() => {
  if (route.name !== 'ChatShowPage') {
    return null
  }

  const conversationId = Number(route.params.conversation)

  return Number.isInteger(conversationId) && conversationId > 0 ? conversationId : null
})
const openConversations = computed(() => {
  return conversations.value.filter((conversation) => {
    return ['open', 'minimized'].includes(conversation.current_participant?.chat_window_state) && Number(conversation.id) !== routeConversationId.value
  })
})
const orderedOpenConversations = computed(() => {
  if (preferredConversationId.value === null) {
    return openConversations.value
  }

  return [...openConversations.value].sort((firstConversation, secondConversation) => {
    if (Number(firstConversation.id) === preferredConversationId.value) {
      return -1
    }

    if (Number(secondConversation.id) === preferredConversationId.value) {
      return 1
    }

    return 0
  })
})
const visibleOpenConversations = computed(() => orderedOpenConversations.value.slice(0, maximumVisibleWindows.value))
const hiddenOpenConversationCount = computed(() => Math.max(0, openConversations.value.length - visibleOpenConversations.value.length))
const totalUnreadCount = computed(() => {
  return conversations.value.reduce((total, conversation) => total + unreadMessageCount(conversation), 0)
})
const filteredConversations = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase()

  if (!query) {
    return conversations.value
  }

  return conversations.value.filter((conversation) => {
    return conversationTitle(conversation).toLocaleLowerCase().includes(query)
  })
})

const loadConversations = async () => {
  dockError.value = ''

  try {
    await conversationStore.fetchConversations({ clear: true, per_page: 100 })
    chatStore.subscribeToConversations(conversations.value.map((conversation) => conversation.id))
  } catch (error) {
    dockError.value = errorMessageFrom(error, 'Unable to load conversations.')
  }
}

const openConversation = async (conversation) => {
  dockError.value = ''

  try {
    if (conversation.current_participant?.chat_window_state !== 'open') {
      await participantStore.setChatWindowState(conversation.id, 'open')
    }

    preferredConversationId.value = Number(conversation.id)
    chatStore.setActiveConversation(conversation.id)
    isLauncherOpen.value = false
  } catch (error) {
    dockError.value = errorMessageFrom(error, 'Unable to open this chat window.')
  }
}

const isUpdatingWindow = (conversationId) => {
  return participantStore.updatingWindowConversationIds.includes(Number(conversationId))
}

const unreadMessageCount = (conversation) => {
  return Math.max(0, Number(conversation.current_participant?.unread_count || 0))
}

const hasUnreadMessages = (conversation) => {
  return unreadMessageCount(conversation) > 0
}

const conversationTitle = (conversation) => {
  if (conversation.title) {
    return conversation.title
  }

  const otherParticipant = conversation.participants?.find((participant) => Number(participant.user_id) !== Number(authStore.user?.id))

  return otherParticipant?.user?.name || 'Direct conversation'
}

const conversationInitials = (conversation) => {
  return conversationTitle(conversation)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'C'
}

const lastMessagePreview = (conversation) => {
  return conversation.last_message?.body || conversation.last_message?.type || 'No messages yet'
}

const updateMaximumVisibleWindows = () => {
  if (window.innerWidth < 1100) {
    maximumVisibleWindows.value = 1
  } else if (window.innerWidth < 1500) {
    maximumVisibleWindows.value = 2
  } else {
    maximumVisibleWindows.value = 3
  }
}

const errorMessageFrom = (error, fallback) => {
  return error?.response?.data?.message || error?.message || fallback
}

const initializeChat = () => {
  if (hasInitializedChat.value) {
    return
  }

  hasInitializedChat.value = true
  browserNotificationStore.initialize()
  chatStore.subscribeToGlobalPresence()
  loadConversations()
}

const tearDownChat = () => {
  if (!hasInitializedChat.value) {
    return
  }

  chatStore.unsubscribeFromGlobalPresence()
  chatStore.unsubscribeFromAllConversations()
  hasInitializedChat.value = false
}

watch(canUseChat, (isAllowed) => {
  if (isAllowed) {
    initializeChat()
  } else {
    tearDownChat()
  }
}, { immediate: true })

watch(
  () => visibleOpenConversations.value.map((conversation) => Number(conversation.id)),
  (visibleConversationIds) => {
    const activeConversationId = Number(chatStore.activeConversationId)

    if (activeConversationId > 0 && activeConversationId !== routeConversationId.value && !visibleConversationIds.includes(activeConversationId)) {
      chatStore.clearActiveConversation()
    }
  }
)

onMounted(() => {
  updateMaximumVisibleWindows()
  window.addEventListener('resize', updateMaximumVisibleWindows)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMaximumVisibleWindows)
  tearDownChat()
})
</script>
